import React, {
    useEffect,
    useMemo,
    useState,
    useCallback,
    useRef,
} from 'react';
import PropTypes from 'prop-types';
import _isEmpty from 'lodash/isEmpty';

import Metadata from 'bdvp-common/src/components/Metadata';
import NotFound from 'bdvp-common/src/components/NotFound';
import Error from 'bdvp-common/src/components/Error';
import DisplayAds from 'bdvp-common/src/utils/displayAds';
import { scrollRestore } from 'bdvp-common/src/utils';

import Ad from 'bdvp-common/src/components/Ad';
import NotificationRenderer from 'bdvp-common/src/components/NotificationRenderer';
import { useDidUpdate } from 'bdvp-common/src/hooks';
import {
    normalizeMediaItem,
    getMediaPath,
} from 'bdvp-common/src/utils';
import { Tracking } from 'bdvp-tracking';
import { usePrevious } from 'bdvp-common/src/hooks';
import { useMediaConfigPlayback } from 'bdvp-common/src/hooks/config';

import Template from '../Template';
import Head from '../Head';
import Player from '../Player';
import Playlist from '../Playlist';
import BackToLink from './BackToLink';

import {
    pauseVideoPlayback,
    playVideoPlayback,
    playClip,
} from '../Player/utils';
import Notification from './Notification';

/**
 * This @PlaylistKey represent the playlist order in the page.
 * So far, we only have one playlist, so we are going to hard-code this value.
 * Later on, if we have more than one list, we can build/use the index list's array
 * to get that key from.
 */
const PLAYLIST_KEYS = {
    PLAYLIST_1: 0,
};

export const getPlaylistItems = (mediaItem, list, isSearch = false) => {

    if(isSearch) {

        const itemIndex = list.findIndex(item => item.slug === mediaItem.slug);

        if (itemIndex === -1) {

            return [
                mediaItem,
                ...list,
            ];

        }

        return list.slice(itemIndex, list.length);

    }

    return [
        mediaItem,
        ...list.filter(item => item.slug !== mediaItem.slug),
    ];

};

const PlaybackPage = ({
    isEU,
    isPlaybackReady,
    mbox,
    clipData,
    clipError,
    clipLoading,
    feedType,
    topicId,
    topicLoading,
    topicLoadingMore,
    topicData,
    topicError,
    topicFetchMore,
    location,
    history,
    clipSlug,
    isShareModalOpen,
    isSearch,
    isFilterActive,
    playlistTitle,
}) => {

    const config = useMediaConfigPlayback();
    const { displayAd: displayAdConfig = {} } = config;
    const { topicPlayList = {} } = topicData;
    const mediaItem = useMemo(() => {

        const mediaPlayback = clipData?.mediaPlayback?.[0];
        return normalizeMediaItem(mediaPlayback, feedType);

    }, [
        clipData,
        feedType,
    ]);

    const mediaItemList = useMemo(() => (
        (topicPlayList.mediaPlayback ?? []).map(item => normalizeMediaItem(item))
    ), [topicPlayList.mediaPlayback]);

    const [
        nowPlayingItem,
        setNowPlayingItem,
    ] = useState(mediaItem);

    const [
        playlistItems,
        setPlaylistItems,
    ] = useState(
        _isEmpty(mediaItem)
            ? []
            : getPlaylistItems(mediaItem, mediaItemList, isSearch)
    );

    const prevPlaylistItems = usePrevious(playlistItems);

    const playlistItemsRef = useRef(playlistItems);

    const [
        playerWrapper,
        setPlayerWrapper,
    ] = useState(null);

    useEffect(() => {

        DisplayAds.init(displayAdConfig);
        Tracking.trackPlaybackPageView();
        scrollRestore();

        return () => {

            DisplayAds.destroySlots(displayAdConfig);

        };

    }, [displayAdConfig]);

    useEffect(() => {

        let unsubscribeFromHistory = () => {};

        if (playerWrapper) {

            unsubscribeFromHistory = history.listen((newLocation, action) => {

                if (action === 'POP') {

                    const newSlug = newLocation.pathname.replace('/', '');
                    const clip = playlistItemsRef.current.find(playlistItem => playlistItem.slug === newSlug);
                    playClip(clip, playerWrapper);

                }

            });

        }

        return () => {

            unsubscribeFromHistory();

        };

    }, [
        history,
        playlistItemsRef,
        playerWrapper,
    ]);

    useEffect(() => {

        playlistItemsRef.current = playlistItems;

    }, [playlistItems]);

    // Set playlistItems when topicData and clipData are both loaded
    useDidUpdate(() => {

        const newPlaylist = _isEmpty(mediaItem)
            ? []
            : getPlaylistItems(mediaItem, mediaItemList, isSearch);
        setPlaylistItems(newPlaylist);

    }, [
        mediaItem,
        mediaItemList,
        setPlaylistItems,
    ]);

    // Set nowPlayingItem when mediaItem changes -> when clipData is loaded
    useDidUpdate(() => {

        setNowPlayingItem(mediaItem);

    }, [
        mediaItem,
        setNowPlayingItem,
    ]);

    // Update nowPlayingItem with browser back button
    useDidUpdate(() => {

        const newNowPlaying = playlistItems.find((item) => item.slug === clipSlug);

        if (!newNowPlaying) return;

        setNowPlayingItem(newNowPlaying);

    }, [
        clipSlug,
        playlistItems,
        setNowPlayingItem,
    ]);

    // Update player's playlist when playlistItems change
    useDidUpdate(() => {

        const playlistWasUpdated = (playerWrapper && prevPlaylistItems !== playlistItems);
        const playerPlaylistWasNotUpdated = (playerWrapper && playerWrapper.config.list.length === 0 && playlistItems.length > 0);

        if (
            playlistWasUpdated || playerPlaylistWasNotUpdated
        ) {

            playerWrapper.updatePlaylist(playlistItems, nowPlayingItem.slug);

        }

    }, [
        playlistItems,
        playerWrapper,
    ]);

    // Pause or Play video when share modal opens or closes
    useDidUpdate(() => {

        if(isShareModalOpen) {

            pauseVideoPlayback();

        } else {

            playVideoPlayback();

        }

    }, [
        isShareModalOpen,
        pauseVideoPlayback,
        playVideoPlayback,
    ]);

    const playlistItemClickHandler = useCallback((event, item, index) => {

        event.preventDefault();

        Tracking.onPlaylistItemClick(index, PLAYLIST_KEYS.PLAYLIST_1, item.slug);
        playClip(item, playerWrapper);
        setNowPlayingItem(item);
        scrollRestore({ behavior: 'smooth' });

    }, [
        setNowPlayingItem,
        playerWrapper,
    ]);

    const onMetaDataLoadedHandler = useCallback((prevMediaItem, nextMediaItem) => {

        setNowPlayingItem(nextMediaItem);

        const newMediaPath = getMediaPath(nextMediaItem);
        const newPathname = `/${newMediaPath}`;

        if (history.location.pathname === newPathname) return;

        history.push({
            pathname: newPathname,
            search: location.search,
        }, location.state);

    }, [
        setNowPlayingItem,
        history,
        location,
    ]);

    const searchParamsCallback = useCallback(() => {

        return location.search;

    }, [location.search]);

    if (!clipLoading && !mediaItem.id) {

        return <NotFound />;

    }

    if (clipError) {

        return <Error />;

    }

    const player = (
        <Player
            containerId={'playback-page-player'}
            isEU={isEU}
            isLoading={clipLoading}
            isPlaybackReady={isPlaybackReady}
            mbox={mbox}
            mediaItem={nowPlayingItem}
            mediaItemList={playlistItems}
            onMetaDataLoaded={onMetaDataLoadedHandler}
            topicId={topicId}
            setPlayerWrapper={setPlayerWrapper}
        />
    );

    const metadata = (
        <Metadata
            mediaPlayback={nowPlayingItem}
            onMetadataExpand={() => {

                Tracking.onMetadataToggle();

            }}
            onTagClick={(query, tagUrl) => {

                const url = `${tagUrl.pathname}${tagUrl.search}`;
                Tracking.onSearchTagClick(query, url);

            }}
            {...(clipData.userInfo && { userInfo: clipData.userInfo })}
        />
    );

    const adPageTargeting = DisplayAds.updatePageTargetingPageIdWithTopicId(displayAdConfig, topicId);
    const adIsResponsive = displayAdConfig.responsive;

    const playlistAdConfig = DisplayAds.lookupDisplayAd(displayAdConfig, 'playlist-ad');
    const playlistAd = (
        <Ad
            pageTargeting={adPageTargeting}
            isResponsive={adIsResponsive}
            adSlots={playlistAdConfig?.slots}
            targeting={playlistAdConfig?.targeting}
        />
    );

    const playlist = (
        <Playlist
            isLoading={clipLoading || topicLoading}
            hasMore={topicPlayList.hasMore}
            loadingMore={topicLoadingMore}
            loadMore={topicFetchMore}
            initialMediaItem={mediaItem}
            mediaItem={nowPlayingItem}
            mediaItemList={playlistItems}
            onItemClick={playlistItemClickHandler}
            topicTitle={playlistTitle || topicPlayList.title}
            sponsorshipAd={playlistAd}
            topicId={topicId}
            modelType={topicPlayList.modelType}
            modelVersion={topicPlayList.modelVersion}
            puid={topicPlayList.puid}
            searchParams={searchParamsCallback}
            topicError={topicError}
        />
    );

    const bannerAdConfig = DisplayAds.lookupDisplayAd(displayAdConfig, 'banner-ad');
    const bannerAd = (
        <Ad
            pageTargeting={adPageTargeting}
            isResponsive={adIsResponsive}
            adSlots={bannerAdConfig?.slots}
            targeting={bannerAdConfig?.targeting}
        />
    );

    const companionAdConfig = DisplayAds.lookupDisplayAd(displayAdConfig, 'companion-ad');
    const companionAd = (
        <Ad
            pageTargeting={adPageTargeting}
            isResponsive={adIsResponsive}
            adSlots={companionAdConfig?.slots}
            targeting={companionAdConfig?.targeting}
            syncWithVideoAd={companionAdConfig?.syncWithVideoAd}
        />
    );

    return (
        <>
            <Head mediaItem={nowPlayingItem} />
            <Template
                metadata={metadata}
                player={player}
                playlist={playlist}
                companionAd={companionAd}
                bannerAd={bannerAd}
                notification={
                    <NotificationRenderer
                        config={config}
                        topicId={topicId}
                        render={(id, contentConfig) => (
                            <Notification id={id} contentConfig={contentConfig} />
                        )}
                    />
                }
                isFilterActive={isFilterActive}
                backToLink={<BackToLink />}
            />
        </>
    );

};

PlaybackPage.propTypes = {
    clipLoading: PropTypes.bool.isRequired,
    clipSlug: PropTypes.string.isRequired,
    isPlaybackReady: PropTypes.bool.isRequired,
    topicId: PropTypes.string.isRequired,
    topicLoading: PropTypes.bool.isRequired,
    topicLoadingMore: PropTypes.bool.isRequired,
    topicFetchMore: PropTypes.func.isRequired,
    clipData: PropTypes.shape({
        mediaPlayback: PropTypes.array,
    }),
    clipError: PropTypes.object,
    isEU: PropTypes.bool,
    mbox: PropTypes.object,
    topicData: PropTypes.shape({
        topicPlayList: PropTypes.shape({
            hasMore: PropTypes.bool,
            title: PropTypes.string,
            mediaPlayback: PropTypes.array,
        }),
    }),
    topicError: PropTypes.object,
    isShareModalOpen: PropTypes.bool,
    isSearch: PropTypes.bool,
    isFilterActive: PropTypes.bool.isRequired,
    playlistTitle: PropTypes.string,
};

PlaybackPage.defaultProps = {
    clipData: {},
    isEU: false,
    mbox: {},
    topicData: {},
    isShareModalOpen: false,
    isSearch: false,
    playlistTitle: undefined,
};

export default PlaybackPage;
